import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom, Observable, of, tap } from 'rxjs';
import { EnvService } from 'src/app/env.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { StorageService } from 'src/app/storage.service';
import { ContentService } from 'src/app/views/content/content.service';
import { ChunkService } from '../../content/chunk/chunk.service';

interface NoteCreated {
  id: string;
  contentId: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotesService {
  url;
  constructor(
    private http: HttpClient,
    private env: EnvService,
    private translate: TranslateService,
    private errorHandler: ErrorHandlerService,
    private toast: ToastService,
    private storageService: StorageService,
    private contentService: ContentService,
    private chunkService: ChunkService
  ) {
    this.url = this.env.apiUrl + 'notes/';
  }
  private noteSource = new BehaviorSubject<any>({});
  note$ = this.noteSource.asObservable();

  sendNoteEvent(event) {
    this.noteSource.next(event);
  }
  async getAllNotes(data, reload = true): Promise<Observable<any>> {
    let savedNotesList: any = await this.storageService.find('notesList', [`${data.pageIndex}_${data.pageSize}`]);
    if (savedNotesList && savedNotesList.length && !reload) {
      return of(savedNotesList[0]);
    }
    return this.http.get(this.url + 'get-all/' + data.pageSize + '/' + data.pageIndex).pipe(
      tap((res: any) => {
        this.contentService.saveNotesList(res, data.pageIndex, data.pageSize);
      })
    );
  }
  copyToCourse(data: { noteId: string; courseId: string; lectureName: string }) {
    return this.http.post(this.url + 'copy-to-course', data);
  }
  updateSettings(id: string, settings: any) {
    return this.http.post(this.url + 'update-settings', { id, settings });
  }
  async getSavedNotes(data) {
    let savedNotesList: any = await this.storageService.find('notesList', [`${data.pageIndex}_${data.pageSize}`]);
    return savedNotesList ? savedNotesList : [];
  }

  search(data) {
    return this.http.get(this.url + 'search/' + data.pageSize + '/' + data.pageIndex + '/' + (data.searchString.trim().length == 0 ? '-null-' : data.searchString));
  }

  async getNote(id): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const savedNote: any = await this.storageService.find('notes', [id]);
      var done = false;
      if (savedNote.length) {
        done = true;
        resolve(savedNote[0]);
      }
      this.http.get(this.url + 'get/' + id).subscribe(async (res: any) => {
        this.storageService.store('notes', res);
        if (!done) {
          resolve(res);
        }
      });
    });
  }

  createNote(data) {
    return this.http.post(this.url + 'create', data);
  }

  async createNewNote(): Promise<NoteCreated | null> {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    const noteTitle = `${year}-${month}-${day} ${hours}:${minutes}`;
    const data = {
      name: noteTitle,
      description: '',
    };

    try {
      const doc = await firstValueFrom(this.http.post<NoteCreated>(this.url + 'create', data));
      const chunkData = {
        content: '',
        type: 'html',
        language: this.translate.currentLang || 'fr',
        contentID: doc.contentId,
        noteID: doc.id,
        generateVoice: false,
        generateTranslation: false,
        generateEmbeddings: false,
      };
      await firstValueFrom(this.chunkService.createChunk({ chunks: [chunkData] }));
      this.sendNoteEvent('new-note');
      this.toast.showWithoutHeader({ level: 'success', text: this.translate.instant('Snackbar.NoteCreated') });
      return doc;
    } catch (err) {
      this.errorHandler.handle401Error(err);
      console.error(err);
      return null;
    }
  }

  editNote(data) {
    return this.http.post(this.url + 'edit', data).pipe(
      tap(() => {
        this.storageService.remove('notes', [data._id]);
      })
    );
  }

  deleteNote(data) {
    return this.http.post(this.url + 'delete', data);
  }
}
